import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import userBadge from '../../../assets/user_badge.svg';
import { SGSButton, SGSForm, SGSFormDate, SGSFormSelect, SGSInput, SGSSwitch, SGSTooltip } from 'components';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { affilieService, statusCommand } from 'core/services';
import { useHistory, useParams } from 'react-router';
import { RadiationForm, RadiationReason } from 'core/models';
import { toast } from 'react-toastify';
import { useAppContext } from 'core/contexts';
import { useQuery } from 'react-query';
import { useQueryConf } from 'core/utils/useQuery';
import { Box, Typography } from '@material-ui/core';
import { mercure } from 'core/utils';
import { useNotificationContext } from 'features/notification/contexts/NotificationProvider';
import { useRadiationNotifsContext } from 'core/contexts/RadiationProvider';
import styles from './radiation.module.scss';
import classnames from 'classnames';
import { Alert } from '@material-ui/lab';
import { enumStatusCommand } from 'core/services/statusCommand';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import SGSPageLoader from 'components/SGSPageLoader';
import { isVisitor } from 'core/utils/roles';

type Props = {};

const RadiationPage: React.FC<Props> = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { codAffilie, codSouscrip } = useParams<any>();
    const [certified, setCertified] = useState(false);
    const { user } = useAppContext();
    const { addNotification } = useNotificationContext();
    const [eventList, _setEventList] = useState([]);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const { setRadiationNewEvent, radiationNotifs, addRadiationNotif } = useRadiationNotifsContext();

    const eventListRef = useRef(eventList);
    const setEventList = (data) => {
        eventListRef.current = data;
        _setEventList(data);
    };

    const hasRole = (user, requiredRoles) => {
        return requiredRoles.some((requiredRole) => user.roles.includes(requiredRole));
    };

    useEffect(() => {
        if (!hasRole(user, ['ROLE_RADIATION', 'ROLE_ADMIN'])) {
            // Redirect to unauthorized page
            // TODO :: create page
            history.push('/unauthorized');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.roles, history]);

    const { isLoading, data } = useQuery(
        [user.login, 'radiation', codAffilie, codSouscrip],
        async () => {
            return await affilieService.getRadiationData({ codAffilie, codSouscrip });
        },
        { ...useQueryConf, cacheTime: 0 },
    );

    const getOnGoingCancelAffilie = useCallback(async (keyList: any) => {
        if (keyList.length) {
            // Get ongoing commands with status "ValidateBiaCommand"
            await statusCommand.getCommand(keyList, [enumStatusCommand.CANCEL_AFFILIE]).then((response: any) => {
                const commandList = [];
                if (response[enumStatusCommand.CANCEL_AFFILIE]) {
                    // For each ongoing command, add it to the commandList and create a mercure listener for it
                    response[enumStatusCommand.CANCEL_AFFILIE].forEach((c) => {
                        const command = JSON.parse(c.data);
                        commandList.push({ id: c.id, data: command });
                        createMercureListener(c.id);
                    });
                }
                addRadiationNotif(commandList);
            });
        }
    }, []);

    /**
     * Check if a cancellation is in progress
     */
    useEffect(() => {
        const keyList = [codAffilie + '-' + codSouscrip];
        getOnGoingCancelAffilie(keyList);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isLoadingData = isLoading;

    // Get cancelled contracts in treatment for current affiliate
    const onGoingCancelled = radiationNotifs.length
        ? radiationNotifs.filter((r) => r.data.codAffilie === codAffilie).flatMap((c) => c.data?.contracts?.map((contract) => contract.idContract) || [])
        : [];

    const affilie = data?.affilie;
    const souscrip = data?.souscrip;
    let contracts = data?.contracts.filter((c) => !onGoingCancelled.includes(c.id)) ?? [];

    const santeContracts = contracts.filter((contract) => contract.risk === 'Santé');
    const prevContracts = contracts.filter((contract) => contract.risk === 'Prévoyance');

    const validationSchema: Yup.SchemaOf<RadiationForm> = Yup.object().shape({
        codAffilie: Yup.string().required(),
        codSouscrip: Yup.string().required(),
        motif: Yup.mixed().oneOf(Object.values(RadiationReason), t('yup.validators.required')).required(),
        dateEffective: Yup.date().required(),
        portability: Yup.string().test({
            message: 'Veuillez renseigner une durée',
            test: (value) => {
                const duration = Number(value);
                if (watchMotif !== RadiationReason.CONTRACT_END_WITH_INDEMNISATION) return true;
                if (duration > 0 && duration < 13) return true;
                return false;
            },
        }),
        contracts: Yup.array()
            .of(
                Yup.object().shape({
                    idContract: Yup.string().required('ID du contrat requis'),
                }),
            )
            .test({
                message: 'Au moins un contrat est requis',
                test: (arr) => {
                    return arr.length >= 1;
                },
            }),
    });

    const methods = useForm<RadiationForm>({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            codAffilie,
            codSouscrip,
            dateEffective: new Date(),
            portability: '0',
            contracts: [],
        },
    });
    const {
        handleSubmit,
        watch,
        setValue,
        trigger,
        formState: { errors },
    } = methods;
    const watchMotif = watch('motif');

    useEffect(() => {
        // Reset portability if not CMU
        if (watchMotif !== RadiationReason.CONTRACT_END_WITH_INDEMNISATION) setValue('portability', '0');
        // Validate form field contract
        if (watchMotif) trigger('contracts');
    }, [watchMotif, setValue, trigger]);

    useEffect(() => {
        // Get only health contract for CMU and set them to form
        const contractsInForm = watchMotif === RadiationReason.CMU ? santeContracts : contracts;
        const contractIds = contractsInForm.map((c) => {
            return { idContract: parseInt(c.id) };
        });
        setValue('contracts', contractIds);
    }, [contracts, santeContracts, setValue, watchMotif]);

    const mercureFunction = async (event) => {
        const result = event.result;
        let affilie = null;
        if (result?.affilie?.prenom && result?.affilie?.nom) affilie = result.affilie.prenom + ' ' + result.affilie.nom;
        // Si event OK
        if (event.Status === 'OK' && !result.hasError) {
            toast.success('La salarié ' + affilie + ' est radié avec succès');
        } else {
            addNotification({ commandId: event.id, content: result, type: enumStatusCommand.CANCEL_AFFILIE });
        }
        // update data
        setRadiationNewEvent(result);
    };

    /**
     * Create a Mercure listener for the specified ID.
     * @param {string} id - The ID of the Mercure event stream.
     */
    const createMercureListener = (id) => {
        const eventSource = mercure.createListener(id, mercureFunction);
        setEventList([...eventListRef.current, eventSource]);
    };

    function sendForm(values: RadiationForm) {
        setIsSubmitted(true);
        // Check portability
        if (watchMotif !== RadiationReason.CONTRACT_END_WITH_INDEMNISATION) values.portability = '0';
        // Remove Prev contract if CMU
        if (watchMotif === RadiationReason.CMU) {
            const contractsSanteIds = contracts.filter((c) => c.risk === 'Santé').map((c) => c.id);
            const contractSanteToCancel = values.contracts.filter((contract) => contractsSanteIds.includes(contract.idContract));
            values.contracts = contractSanteToCancel;
        }

        // add firstname + lastname to the form
        const formData: any = values;
        formData.nom = data.affilie.nom;
        formData.prenom = data.affilie.prenom;

        // TODO :: update symfony constraint for date only
        // formData.dateEffective = moment(formData.dateEffective).format('YYYY-MM-DD');

        affilieService
            .sendRadiationForm(formData)
            .then((response) => {
                createMercureListener(response.id);
                toast.success(t('radiate.sendSuccess'));
                history.push('/affiliations?noreset=true');
            })
            .catch(() => {
                toast.error(t('radiate.sendError'));
            })
            .finally(() => {
                setIsSubmitted(false);
            });
    }

    const handleCancel = () => {
        history.push('/affiliations?noreset=true');
    };

    const monthOptions = Array.from({ length: 12 }, (_, index) => ({
        label: (index + 1).toString(),
        value: (index + 1).toString(),
    }));

    const boxAffilie = () => {
        return (
            <>
                <Box display="flex">
                    <Box height={50} alignItems="center">
                        <img src={userBadge} alt="userBadge" className={styles.userBadge} />
                    </Box>
                    <Box flexGrow={1} height={50} className="ml-3">
                        <Box>
                            <span className={styles.affilie}>
                                {affilie?.nom} {affilie?.prenom}
                            </span>
                        </Box>
                        <Box>
                            <span className={styles.souscrip}>{souscrip?.nomSouscripteur}</span>
                        </Box>
                    </Box>
                </Box>
                <Box></Box>
            </>
        );
    };

    const displayContracts = () => {
        return (
            <>
                {santeContracts.length > 0 && (
                    <>
                        <Box>
                            <Typography className={styles.risk}>
                                <strong>Santé</strong>
                            </Typography>
                            {santeContracts.map((contract) => (
                                <Typography key={contract.label} className={styles.contrat}>
                                    {contract.label} - {contract.type}
                                </Typography>
                            ))}
                        </Box>
                    </>
                )}

                {prevContracts.length > 0 && (
                    <>
                        <Box className={classnames('mt-3', watchMotif === RadiationReason.CMU ? styles.diseabledContracts : '')}>
                            <Typography className={styles.risk}>
                                <strong>Prévoyance</strong>
                            </Typography>
                            {prevContracts.map((contract) => (
                                <Typography key={contract.label} className={styles.contrat}>
                                    {contract.label} - {contract.type}
                                </Typography>
                            ))}
                        </Box>

                        {watchMotif === RadiationReason.CMU ? (
                            <Box className={styles.alertCmu}>
                                <Alert severity="info">En cas de sortie suite à une adhésion à la CMU, la radiation ne s'appliquera qu'aux contrats Santé</Alert>
                            </Box>
                        ) : null}
                    </>
                )}
            </>
        );
    };

    const motifsOptions = () => {
        let options = Object.keys(RadiationReason).map((o) => ({
            label: RadiationReason[o],
            value: RadiationReason[o],
        }));
        if (santeContracts.length === 0) {
            options = options.filter((entry) => entry.value !== RadiationReason.CMU);
        }
        return options;
    };

    const redirectToDocumentation = () => {
        window.open('/docs/Guide portabilité.pdf', '_blank', 'noreferrer');
    };
    return (
        <>
            {isLoadingData ? (
                <SGSPageLoader />
            ) : (
                <FormProvider {...methods}>
                    <SGSForm.Card onSubmit={handleSubmit(sendForm)} maxWidth={'lg'} style={{ paddingLeft: '50px', paddingRight: '50px', paddingTop: '20px' }}>
                        <SGSForm.Row>
                            <SGSForm.Col md={6}>
                                {/* Column left */}
                                {boxAffilie()}
                            </SGSForm.Col>
                            <SGSForm.Col md={6}>
                                {/* Column right */}
                                <Box className="text-right">
                                    <Box className={styles.boxIcon} onClick={() => redirectToDocumentation()}>
                                        <SGSTooltip title={<Box>{'Documentation'}</Box>}>
                                            <FontAwesomeIcon icon={faQuestionCircle} size="lg" className={styles.IconHelp} />
                                        </SGSTooltip>
                                    </Box>
                                </Box>
                            </SGSForm.Col>
                        </SGSForm.Row>

                        <SGSForm.Row>
                            <SGSForm.Col md={6}>
                                <SGSFormDate label="radiate.fields.date" inputVariant="outlined" name="dateEffective" className={styles.responsive_datepicker} />
                            </SGSForm.Col>
                            <SGSForm.Col md={6}>
                                <SGSFormSelect label="radiate.fields.reason" variant="outlined" name="motif" options={motifsOptions()} />
                            </SGSForm.Col>
                        </SGSForm.Row>

                        <SGSForm.Row>
                            <SGSForm.Col md={6}>
                                {displayContracts()}
                                {contracts && errors?.contracts?.message && (
                                    <Box className={styles.alertCmu}>
                                        <Alert severity="error">{errors.contracts.message}</Alert>
                                    </Box>
                                )}
                                {contracts.length === 0 && (
                                    <Box className={styles.alertCmu}>
                                        <Alert severity="error">Aucun contrat de cet assuré n'est éligible à la résiliation</Alert>
                                    </Box>
                                )}
                            </SGSForm.Col>
                            <SGSForm.Col md={6}>
                                {watchMotif === RadiationReason.CONTRACT_END_WITH_INDEMNISATION && (
                                    <>
                                        <SGSFormSelect label="radiate.fields.portability" variant="outlined" name="portability" options={monthOptions} />
                                        <Box style={{ marginBottom: '-56px' }}></Box>
                                    </>
                                )}
                                {/* {watchMotif === RadiationReason.CMU ? ( // <Box className={styles.msgCmu}>En cas de sortie suite à une adhésion à la CMU, la radiation ne portera que sur les contrats de type Santé</Box>
                                    <Box style={{ height: '56px' }}></Box>
                                ) : (
                                    )} */}
                                <Box style={{ height: '56px' }}></Box>
                                <Box>
                                    <Typography className={styles.responsable}>
                                        <strong>Responsable</strong>
                                    </Typography>
                                </Box>
                                <SGSForm.Row>
                                    <SGSForm.Col md={12}>
                                        <SGSInput label={t('radiate.fields.accountableName')} variant="outlined" disabled value={user.nom} />
                                    </SGSForm.Col>
                                </SGSForm.Row>
                                <SGSForm.Row>
                                    <SGSForm.Col md={12}>
                                        <SGSInput label={t('radiate.fields.accountableFirstname')} variant="outlined" disabled value={user.prenom} />
                                    </SGSForm.Col>
                                </SGSForm.Row>
                                <SGSForm.Row>
                                    <SGSForm.Col md={12}>
                                        <SGSInput label={t('radiate.fields.accountableEmail')} variant="outlined" disabled value={user.login} />
                                    </SGSForm.Col>
                                </SGSForm.Row>
                            </SGSForm.Col>
                        </SGSForm.Row>
                        <SGSForm.Actions>
                            <SGSForm.Row>
                                <SGSForm.Col md={6}>
                                    <Box className="mt-1">
                                        <SGSSwitch label={t('radiate.fields.certified')} checked={certified} onChange={(e) => setCertified(e.target.checked)} className={styles.certification} />
                                    </Box>
                                </SGSForm.Col>
                                <SGSForm.Col md={6}>
                                    <Box className="w-100 text-right">
                                        <SGSButton type="button" onClick={() => handleCancel()}>
                                            {t('global.cancel')}
                                        </SGSButton>
                                        <SGSButton disabled={!certified || isSubmitted || contracts.length === 0 || isVisitor(user)} type="submit" className={styles.btnRadier}>
                                            {t('radiate.send')}
                                        </SGSButton>
                                    </Box>
                                </SGSForm.Col>
                            </SGSForm.Row>
                        </SGSForm.Actions>
                    </SGSForm.Card>
                </FormProvider>
            )}
        </>
    );
};

export default RadiationPage;
